import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Users,
  FileText,
  Network,
  Activity,
  ArrowUpRight,
  UserPlus,
  Upload,
  CircleDot,
  AlertCircle,
  Clock,
  CalendarCheck,
  CalendarDays,
  ClipboardList,
  UserCheck,
  Inbox,
  Megaphone,
  Pin,
} from 'lucide-react'
import { apiJson, API_BASE, type Page } from '../api'
import { canManageEmployees, canManageOrgUnits, getUser } from '../auth'

interface Announcement {
  id: string
  title: string
  category: 'news' | 'announcement'
  pinned: boolean
  publishedAt: string
}

interface Employee {
  id: string
  firstName: string
  lastName: string
  employmentStatus: string
  orgUnitId?: string
  createdAt: string
}

interface Doc {
  id: string
  title: string
  category: string
  createdAt: string
}

interface OrgUnit {
  id: string
  name: string
  code?: string
  parentId: string | null
}

interface DashboardAnalytics {
  me: {
    today: { checkInAt: string | null; checkOutAt: string | null; complete: boolean }
    weekMinutes: number
    weekDays: number
    monthMinutes: number
    pendingLeave: number
    approvedLeaveDaysYtd: number
  }
  team: null | {
    totalEmployees: number
    presentToday: number
    pendingApprovals: number
    attendanceTrend: Array<{ date: string; present: number; avgMinutes: number; workDay: boolean }>
    leaveByType: Array<{ type: string; count: number }>
    leaveByStatus: Array<{ status: string; count: number }>
  }
}

function fmtHM(mins: number) {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

const statusMeta: Record<string, { label: string; bar: string; dot: string }> = {
  active: { label: 'Active', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  hired: { label: 'Hired', bar: 'bg-teal-500', dot: 'bg-teal-500' },
  promoted: { label: 'Promoted', bar: 'bg-blue-500', dot: 'bg-blue-500' },
  transferred: { label: 'Transferred', bar: 'bg-sky-500', dot: 'bg-sky-500' },
  applicant: { label: 'Applicant', bar: 'bg-amber-500', dot: 'bg-amber-500' },
  exited: { label: 'Exited', bar: 'bg-slate-400', dot: 'bg-slate-400' },
}

const divisionTints = [
  'from-brand-500 to-brand-700',
  'from-blue-500 to-brand-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
  'from-rose-500 to-pink-600',
]

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return iso.slice(0, 10)
}

export default function Dashboard() {
  const user = getUser()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [employeeTotal, setEmployeeTotal] = useState(0)
  const [documents, setDocuments] = useState<Doc[]>([])
  const [docTotal, setDocTotal] = useState(0)
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([])
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null)
  const [posts, setPosts] = useState<Announcement[]>([])
  const [health, setHealth] = useState('loading')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [empsPage, docsPage, units, stats] = await Promise.all([
          apiJson<Page<Employee>>('/employees?size=500'),
          apiJson<Page<Doc>>('/documents?size=6&sort=createdAt&order=DESC'),
          apiJson<OrgUnit[]>('/org-units'),
          apiJson<DashboardAnalytics>('/analytics/dashboard'),
        ])
        setEmployees(empsPage.data)
        setEmployeeTotal(empsPage.total)
        setDocuments(docsPage.data)
        setDocTotal(docsPage.total)
        setOrgUnits(units)
        setAnalytics(stats)
        setError('')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
      }
      // intranet feed is best-effort — never blocks the dashboard
      apiJson<Page<Announcement>>('/announcements?size=4&sort=publishedAt&order=DESC')
        .then((p) => setPosts(p.data))
        .catch(() => {})
      try {
        const res = await fetch(`${API_BASE}/health`)
        setHealth(res.ok ? 'healthy' : 'unhealthy')
      } catch {
        setHealth('offline')
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [])

  const healthy = health === 'healthy'

  // headcount per top-level division (walk orgUnitId up to the unit directly under root)
  const divisions = useMemo(() => {
    const byId = new Map(orgUnits.map((u) => [u.id, u]))
    const root = orgUnits.find((u) => !u.parentId)
    const counts = new Map<string, number>()
    let unassigned = 0
    for (const emp of employees) {
      let unit = emp.orgUnitId ? byId.get(emp.orgUnitId) : undefined
      if (!unit) {
        unassigned++
        continue
      }
      // climb to the division directly under the root
      while (unit && unit.parentId && unit.parentId !== root?.id) {
        unit = byId.get(unit.parentId)
      }
      const key = unit?.name ?? 'Unassigned'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    const rows = [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
    if (unassigned > 0) rows.push({ name: 'Unassigned', count: unassigned })
    return rows
  }, [employees, orgUnits])

  const maxDivision = Math.max(1, ...divisions.map((d) => d.count))

  const statusRows = useMemo(() => {
    const counts = new Map<string, number>()
    for (const emp of employees) counts.set(emp.employmentStatus, (counts.get(emp.employmentStatus) ?? 0) + 1)
    return Object.keys(statusMeta)
      .filter((s) => counts.has(s))
      .map((s) => ({ status: s, count: counts.get(s)!, ...statusMeta[s] }))
  }, [employees])

  const activity = useMemo(() => {
    const items = [
      ...employees.map((e) => ({
        icon: UserPlus,
        text: `${e.firstName} ${e.lastName} joined as ${statusMeta[e.employmentStatus]?.label ?? e.employmentStatus}`,
        at: e.createdAt,
        tint: 'bg-blue-50 text-blue-600',
      })),
      ...documents.map((d) => ({
        icon: Upload,
        text: `${d.title} was uploaded`,
        at: d.createdAt,
        tint: 'bg-emerald-50 text-emerald-600',
      })),
    ]
    return items
      .filter((i) => i.at)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 6)
  }, [employees, documents])

  const statCards = [
    { label: 'Employees', value: employeeTotal, icon: Users, tint: 'bg-blue-50 text-blue-600', to: '/employees' },
    { label: 'Documents', value: docTotal, icon: FileText, tint: 'bg-emerald-50 text-emerald-600', to: '/documents' },
    { label: 'Org Units', value: orgUnits.length, icon: Network, tint: 'bg-brand-100 text-brand-700', to: '/org-units' },
    {
      label: 'API Health',
      value: healthy ? 'Online' : 'Offline',
      icon: Activity,
      tint: healthy ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600',
      live: true as const,
    },
  ]

  const quickActions = [
    ...(canManageEmployees()
      ? [{ to: '/employees', icon: UserPlus, label: 'Add employee', desc: 'Create a new people record', tint: 'from-blue-500 to-brand-600' }]
      : []),
    { to: '/documents', icon: Upload, label: 'Upload document', desc: 'Share files with your team', tint: 'from-emerald-500 to-teal-600' },
    ...(canManageOrgUnits()
      ? [{ to: '/org-units', icon: Network, label: 'Manage org units', desc: 'Edit the org structure', tint: 'from-brand-600 to-brand-700' }]
      : []),
  ]

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h2 className="text-xl font-extrabold tracking-tight sm:text-2xl">
          Welcome back, {user?.name ?? 'there'}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{today} · Here's what's happening across your workspace.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Personal tiles */}
      {analytics && (
        <div className={`grid grid-cols-2 gap-4 ${analytics.team ? 'xl:grid-cols-6' : 'xl:grid-cols-4'}`}>
          {(
            [
              {
                to: '/attendance',
                icon: Clock,
                tint: 'bg-brand-100 text-brand-700',
                value: analytics.me.today.complete
                  ? 'Done for today'
                  : analytics.me.today.checkInAt
                    ? 'Checked in'
                    : 'Not checked in',
                label: 'Attendance today',
              },
              {
                to: '/attendance',
                icon: CalendarCheck,
                tint: 'bg-blue-50 text-blue-600',
                value: fmtHM(analytics.me.weekMinutes),
                label: `This week · ${analytics.me.weekDays} day${analytics.me.weekDays === 1 ? '' : 's'} present`,
              },
              {
                to: '/attendance',
                icon: CalendarDays,
                tint: 'bg-cyan-50 text-cyan-600',
                value: fmtHM(analytics.me.monthMinutes),
                label: 'Worked this month',
              },
              {
                to: '/leave',
                icon: ClipboardList,
                tint: 'bg-amber-50 text-amber-600',
                value: String(analytics.me.pendingLeave),
                label: `Pending requests · ${analytics.me.approvedLeaveDaysYtd}d approved YTD`,
              },
              ...(analytics.team
                ? [
                    {
                      to: '/attendance',
                      icon: UserCheck,
                      tint: 'bg-emerald-50 text-emerald-600',
                      value: `${analytics.team.presentToday}/${analytics.team.totalEmployees}`,
                      label: 'Present today (team)',
                    },
                    {
                      to: '/leave',
                      icon: Inbox,
                      tint: 'bg-rose-50 text-rose-600',
                      value: String(analytics.team.pendingApprovals),
                      label: 'Leave awaiting review',
                    },
                  ]
                : []),
            ] as const
          ).map((tile) => {
            const Icon = tile.icon
            return (
              <Link key={tile.label} to={tile.to}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <div className="flex items-center justify-between">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tile.tint}`}>
                      <Icon size={16} />
                    </span>
                    <ArrowUpRight size={14} className="text-slate-300 transition-colors group-hover:text-brand-500" />
                  </div>
                  <p className="mt-3 truncate text-lg font-extrabold tracking-tight">{tile.value}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{tile.label}</p>
                </div>
              </Link>
            )
          })}
        </div>
      )}

      {/* Company news */}
      {posts.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-bold tracking-tight">
              <Megaphone size={15} className="text-brand-600" /> Company news
            </h3>
            <Link to="/intranet" className="flex items-center gap-1 text-xs font-semibold text-brand-600 transition-colors hover:text-brand-700">
              View all <ArrowUpRight size={13} />
            </Link>
          </div>
          <div className="mt-3 divide-y divide-slate-50">
            {posts.map((a) => (
              <Link key={a.id} to="/intranet" className="group flex items-center gap-3 py-2.5">
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${
                    a.category === 'news'
                      ? 'bg-sky-50 text-sky-700 ring-sky-100'
                      : 'bg-brand-50 text-brand-700 ring-brand-100'
                  }`}
                >
                  {a.category === 'news' ? 'News' : 'Announcement'}
                </span>
                {a.pinned && <Pin size={12} className="shrink-0 text-amber-500" />}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700 transition-colors group-hover:text-brand-700">
                  {a.title}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-slate-400">{a.publishedAt.slice(0, 10)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Team analytics (admin/hr) */}
      {analytics?.team && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {/* Attendance trend */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold tracking-tight">Team attendance — last 14 days</h3>
              <span className="text-xs text-slate-400">{analytics.team.presentToday} present today</span>
            </div>
            <div className="mt-4 flex h-32 items-end gap-1.5">
              {analytics.team.attendanceTrend.map((d) => {
                const max = Math.max(1, ...analytics.team!.attendanceTrend.map((x) => x.present))
                const pct = (d.present / max) * 100
                return (
                  <div
                    key={d.date}
                    className="group relative flex-1"
                    title={`${d.date}${d.workDay ? '' : ' (non-working day)'}: ${d.present} present${d.avgMinutes ? `, avg ${fmtHM(d.avgMinutes)}` : ''}`}
                  >
                    <div
                      className={`w-full rounded-t-md transition-all ${
                        d.present > 0
                          ? `bg-gradient-to-t from-brand-600 to-brand-400 group-hover:brightness-110 ${d.workDay ? '' : 'opacity-40'}`
                          : d.workDay
                            ? 'bg-slate-100'
                            : 'bg-slate-100/50'
                      }`}
                      style={{ height: `${Math.max(d.present > 0 ? pct : 4, 4)}%` }}
                    />
                  </div>
                )
              })}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-slate-400">
              <span>{analytics.team.attendanceTrend[0]?.date.slice(5)}</span>
              <span>{analytics.team.attendanceTrend[analytics.team.attendanceTrend.length - 1]?.date.slice(5)}</span>
            </div>
          </div>

          {/* Leave breakdown */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <h3 className="text-sm font-bold tracking-tight">Leave requests by type</h3>
            <div className="mt-4 space-y-3">
              {analytics.team.leaveByType.map((row, i) => {
                const max = Math.max(1, ...analytics.team!.leaveByType.map((x) => x.count))
                return (
                  <div key={row.type}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-semibold capitalize text-slate-600">{row.type}</span>
                      <span className="text-slate-400">{row.count}</span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r transition-all duration-500 ${divisionTints[i % divisionTints.length]}`}
                        style={{ width: `${(row.count / max) * 100}%` }}
                      />
                    </div>
                  </div>
                )
              })}
              {analytics.team.leaveByType.length === 0 && (
                <p className="py-6 text-center text-xs text-slate-400">No leave requests yet.</p>
              )}
            </div>
            {analytics.team.leaveByStatus.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
                {analytics.team.leaveByStatus.map((s) => (
                  <span key={s.status} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium capitalize text-slate-500">
                    {s.status}: {s.count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => {
          const Icon = card.icon
          const inner = (
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.tint}`}>
                  <Icon size={20} />
                </div>
                {'live' in card && card.live && (
                  <span className="flex items-center gap-1.5 text-xs font-medium">
                    <span className="relative flex h-2 w-2">
                      <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className={`relative inline-flex h-2 w-2 rounded-full ${healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    </span>
                    <span className={healthy ? 'text-emerald-600' : 'text-red-600'}>Live</span>
                  </span>
                )}
                {'to' in card && card.to && (
                  <ArrowUpRight size={16} className="text-slate-300 transition-colors group-hover:text-brand-500" />
                )}
              </div>
              <p className="mt-4 text-2xl font-extrabold tracking-tight">{card.value}</p>
              <p className="mt-0.5 text-sm text-slate-500">{card.label}</p>
            </div>
          )
          return 'to' in card && card.to ? (
            <Link key={card.label} to={card.to}>
              {inner}
            </Link>
          ) : (
            <div key={card.label}>{inner}</div>
          )
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Headcount by division */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold tracking-tight">Headcount by division</h3>
            <span className="text-xs text-slate-400">{employeeTotal} total</span>
          </div>
          <div className="mt-4 space-y-3">
            {divisions.map((d, i) => (
              <div key={d.name}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-600">{d.name}</span>
                  <span className="text-slate-400">{d.count}</span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r transition-all duration-500 ${divisionTints[i % divisionTints.length]}`}
                    style={{ width: `${(d.count / maxDivision) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            {divisions.length === 0 && (
              <p className="py-6 text-center text-xs text-slate-400">No employees yet.</p>
            )}
          </div>
        </div>

        {/* Status breakdown */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="text-sm font-bold tracking-tight">Employment status</h3>
          {employees.length > 0 ? (
            <>
              <div className="mt-4 flex h-3.5 overflow-hidden rounded-full bg-slate-100">
                {statusRows.map((row) => (
                  <div
                    key={row.status}
                    className={`${row.bar} transition-all duration-500`}
                    style={{ width: `${(row.count / employees.length) * 100}%` }}
                    title={`${row.label}: ${row.count}`}
                  />
                ))}
              </div>
              <ul className="mt-4 space-y-2.5">
                {statusRows.map((row) => (
                  <li key={row.status} className="flex items-center gap-2.5 text-sm">
                    <span className={`h-2.5 w-2.5 rounded-full ${row.dot}`} />
                    <span className="flex-1 text-slate-600">{row.label}</span>
                    <span className="font-semibold">{row.count}</span>
                    <span className="w-12 text-right text-xs text-slate-400">
                      {Math.round((row.count / employees.length) * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="py-6 text-center text-xs text-slate-400">No employees yet.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Quick actions */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="text-sm font-bold tracking-tight">Quick actions</h3>
          <div className="mt-4 space-y-2.5">
            {quickActions.map((action) => {
              const Icon = action.icon
              return (
                <Link
                  key={action.label}
                  to={action.to}
                  className="flex items-center gap-3.5 rounded-xl border border-slate-100 p-3 transition-all hover:border-brand-100 hover:bg-brand-50/40"
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm ${action.tint}`}>
                    <Icon size={17} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{action.label}</span>
                    <span className="block truncate text-xs text-slate-400">{action.desc}</span>
                  </span>
                  <ArrowUpRight size={15} className="ml-auto shrink-0 text-slate-300" />
                </Link>
              )
            })}
            {quickActions.length === 0 && (
              <p className="py-4 text-center text-xs text-slate-400">No actions available for your role.</p>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold tracking-tight">Recent activity</h3>
            <CircleDot size={14} className="text-slate-300" />
          </div>
          <ul className="mt-4 divide-y divide-slate-100">
            {activity.map((item, i) => {
              const Icon = item.icon
              return (
                <li key={i} className="flex items-center gap-3.5 py-3 first:pt-0 last:pb-0">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${item.tint}`}>
                    <Icon size={15} />
                  </span>
                  <p className="min-w-0 flex-1 truncate text-sm text-slate-700">{item.text}</p>
                  <span className="shrink-0 text-xs text-slate-400">{relativeTime(item.at)}</span>
                </li>
              )
            })}
            {activity.length === 0 && (
              <li className="py-6 text-center text-xs text-slate-400">No recent activity.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}
